import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import pricesData from "../rate/prices.json";

// useFetchGraphQL ya no se procesa en el submit, pero lo mantengo por si lo usas en otro lado
import { countWeekdays } from "../../utils/dateUtils";
import { dateInSpain, timeToMinutes } from "../../utils/timeUtils";
import FloatingLabelInput from '../common/inputs/FloatingLabelInput';
import FloatingLabelTextarea from '../common/inputs/FloatingLabelTextArea';
import ContactModal from "./ContactModal";

const ContactForm = ({ t }) => {
    const { i18n } = useTranslation();
    const min = (a, b) => (a < b ? a : b);
    const textTitle = t("pages.contact.texts.text2", { returnObjects: true });
    const textName = t("pages.contact.texts.text9", { returnObjects: true });
    const textSubject = t("pages.contact.texts.text4", { returnObjects: true });
    const textBody = t("pages.contact.texts.text5", { returnObjects: true });
    const textButton = t("pages.contact.texts.text6", { returnObjects: true });
    const textP = t("pages.contact.texts.text7", { returnObjects: true });
    const subjects = t("pages.contact.subjects", { returnObjects: true });

    const [subject, setSubject] = useState('');
    const [subjectId, setSubjectId] = useState('');
    const [contactMethod, setContactMethod] = useState('whatsapp');
    const [name, setName] = useState('');
    const [body, setBody] = useState('');
    
    // NUEVO ESTADO: Guarda la fecha seleccionada
    const hoyEnEspana = dateInSpain().toISOString().split('T')[0];
    const [startDate, setStartDate] = useState(hoyEnEspana); 
    const [endDate, setEndDate] = useState(hoyEnEspana); 

    // New state for times
    const [startTime, setStartTime] = useState('07:00');
    const [endTime, setEndTime] = useState('10:00');
    
    const [isSuccess, setIsSuccess] = useState();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const getBookedDays = () => countWeekdays(startDate, endDate);

    

    const calculateInfoButton = () => {
      if (!pricesData) return '';

      if (subjectId === '0') {
        const duration = (timeToMinutes(endTime) - timeToMinutes(startTime)) / 60;
        const price = duration > 0 ? min(duration * pricesData.hour, pricesData.day) : 0;
      
        return t("pages.contact.modal.info_price_hourly", {
          hourly: pricesData.hour,
          day: pricesData.day,
          total: price
        });
      }
      
      if (subjectId === '1') {
        const diffDays = getBookedDays();
        if (diffDays === 0) return '';

        const dailyRate =
          diffDays >= 10 ? pricesData.tendays  :
          diffDays >= 5 ? pricesData.fivedays  :
          pricesData.day;

        const totalPrice = diffDays * dailyRate;

        if (diffDays >= 10) {
          return t("pages.contact.modal.info_price_10days", {
            days: diffDays,
            daily: dailyRate,
            total: totalPrice
          });
        }

        if (diffDays >= 5) {
          return t("pages.contact.modal.info_price_5days", {
            days: diffDays,
            daily: dailyRate,
            total: totalPrice
          });
        }
        return t("pages.contact.modal.info_price_normal", {
          days: diffDays,
          daily: dailyRate,
          total: totalPrice
        });
      }
      return t("pages.contact.modal.info_price_default");
    };

    const handleSubmit = async (e) => {
      e.preventDefault();

      if (subjectId === '2' && !body.trim()) {
        return body;
      }

      setLoading(true);

      try {

        // Convertimos la fecha seleccionada a un formato más amigable (DD/MM/YYYY) para el mensaje
        const startDateFormatted = startDate ? new Date(startDate).toLocaleDateString('es-ES') : '';
        const endDateFormatted = endDate ? new Date(endDate).toLocaleDateString('es-ES') : '';

        // Construimos el texto del mensaje para WhatsApp codificando los saltos de línea (%0A)
        let textMessage = `${t("pages.contact.modal.whatsappSubject")}%0A%0A` +
                            `*${t("pages.contact.modal.subject")}:* ${subject}%0A` +
                            `*${t("pages.contact.modal.name")}:* ${name}%0A`;

        if (subjectId == '0' || subjectId == '1') textMessage += 
                            `*${t("pages.contact.modal.startDate")}:* ${startDateFormatted}%0A `;

        if (subjectId == '1') textMessage += 
                            `*${t("pages.contact.modal.endDate")}:* ${endDateFormatted}%0A`;

        if (subjectId === '0') {
          textMessage += `*${t("pages.contact.modal.startTime")}:* ${startTime}%0A` +
                         `*${t("pages.contact.modal.endTime")}:* ${endTime}%0A`;
        }

        if (subjectId === '2') {
          const freeTextBody = body ? body.replace(/\n/g, '%0A').replace(/\r/g, '') : '';
          textMessage += `*${t("pages.contact.modal.body")}:*%0A${freeTextBody}%0A`;
        }
        else{
        textMessage += `*${t("pages.contact.modal.information_price")}:*%0A- ${calculateInfoButton()}`;
        }

        if (contactMethod === 'whatsapp') {
          const whatsappNumber = t("footer.phone"); 
          const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${textMessage}`;
          window.open(whatsappUrl, '_blank');
          setIsSuccess(true);
        } else {
          const recipientEmail = t("footer.email"); 
          const mailtoSubject = subject;
          // Clean the message for plain text email (remove * used for WhatsApp bold)
          const mailtoBody = decodeURIComponent(textMessage).replace(/\*/g, '');
          
          const mailtoUrl = `mailto:${recipientEmail}?subject=${encodeURIComponent(mailtoSubject)}&body=${encodeURIComponent(mailtoBody)}`;
          
          window.location.href = mailtoUrl;
          setIsSuccess(true);
        }
      } catch (error) {
        setIsSuccess(false);
      } finally {
        setLoading(false);
        setIsModalOpen(true);
      }
    };

    const handleCloseModal = (isSent) => {
      if (isSent) {
        window.location.reload();
      } else {
        setIsModalOpen(false);
      }
    };

    const handleChangeSubject = (selectedId) => {
      const foundEntry = Object.entries(subjects).find(([key, value]) => value.id === selectedId);
      if (foundEntry) {
          const [key, value] = foundEntry;
          setSubject(value.subject);
          setSubjectId(value.id);

          if (value.id === '2') {
            setBody('');
            return;
          }

          setBody(prepareBody(key, value));
      }
    };

    useEffect(() => {
      const hash = window.location.hash.replace("#", "");
      const hashToOption = {
          hour: { value: subjects["hours"], id: "0",nextDate: getFutureDate(0) },
          day: { value: subjects["day"], id: "1",nextDate: getFutureDate(0) },
          fivedays: { value: subjects["day"], id: "1",nextDate: getFutureDate(6) }, // 5 days from today
          tendays: { value: subjects["day"], id: "1",nextDate: getFutureDate(11) },
          other: { value: subjects["other"], id: "2" },
          // month: { value: subjects["month"], id: "3",nextDate: getFutureDate(27) },
          // office: { value: subjects["office"], id: "4",nextDate: getFutureDate(0) },
          // halfday: { value: subjects["halfday"], id: "2", nextDate: getFutureDate(0) },
          
      };

      if (hashToOption[hash]) {
          const foundEntry = hashToOption[hash];
          setSubjectId(foundEntry.id);
          setEndDate(foundEntry.nextDate ? foundEntry.nextDate : dateInSpain()); // Set endDate based on the hash
          handleChangeSubject(foundEntry.id)
      } else {
          const defaultSubject = subjects["hours"];
          setSubjectId(defaultSubject.id);
          handleChangeSubject(defaultSubject.id)
      }
      
      // Añade esta línea justo antes de cerrar el useEffect:
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (subjectId !== '') {
        handleChangeSubject(subjectId);
      }
      
      // Añade esta línea aquí también:
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t]);

    return (
      <>
        <form className="booking-form" onSubmit={handleSubmit}>
            <h2 className="booking-form-title">{textTitle}</h2>
            <div className="generic-separator" />
            <p className="booking-form-p">{textP}</p>
            <div className="input-container">

              <FloatingLabelInput id="name" label={textName} value={name} onChange={(e) => setName(e.target.value)} />

              <div className="input-wrapper">
                  <select
                      className="input-field"
                      value={subjectId}
                      onChange={(e) => handleChangeSubject(e.target.value)}
                  >
                      {Object.entries(subjects).map(([key, value]) => (
                          <option key={key} value={value.id}>
                              {value.subject}
                          </option>
                      ))}
                  </select>
                  <label className="input-label" htmlFor="subject">{textSubject}</label>
                </div>
              {/* NUEVO CAMPO: Selector de fecha futura adaptado a tus componentes */}
              {(subjectId == '0' || subjectId == '1') &&(
                <FloatingLabelInput 
                id="startDate" 
                label={t("pages.contact.modal.startDate")} 
                type="date"
                min={hoyEnEspana} // Bloquea los días anteriores a hoy en el calendario nativo
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)} 
              />)}
              {(subjectId == '1') && (
                <FloatingLabelInput 
                  id="endDate" 
                  label={t("pages.contact.modal.endDate")} 
                  type="date"
                  min={startDate} // Bloquea los días anteriores a hoy en el calendario nativo
                  value={endDate} 
                  onChange={(e) => setEndDate(e.target.value)} 
                />
              )}
              {subjectId === '0' && (
                <>
                  <FloatingLabelInput 
                    id="startTime" 
                    label={t("pages.contact.modal.startTime")} 
                    type="time"
                    step="3600"
                    min="07:00"
                    max="21:00"
                    value={startTime} 
                    onChange={(e) => setStartTime(e.target.value)} 
                  />
                  <FloatingLabelInput 
                    id="endTime" 
                    label={t("pages.contact.modal.endTime")} 
                    type="time"
                    step="3600"
                    min="07:00"
                    max="21:00"
                    value={endTime} 
                    onChange={(e) => setEndTime(e.target.value)} 
                  />
                </>
              )}

              {subjectId === '2' && (
                <FloatingLabelTextarea
                  id="body"
                  label={textBody}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
              )}
              <div className="info-block">
                <div className="info-block-label">{t("pages.contact.modal.information_price")}</div>
                <div className="info-block-text">{calculateInfoButton()}</div>
              </div>
            </div>
            {loading && <div className="loading-spinner" style={{height: "40px"}}><span>{t("pages.contact.texts.text8")}</span></div>}
            
            {!loading && (
              <div className="parallel-buttons-container" style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button 
                  type="submit" 
                  className="booking-form-button" 
                  onClick={() => setContactMethod('whatsapp')}
                  disabled={subject === '' || name === '' || (subjectId === '2' && body.trim() === '')}
                >
                  {t("pages.contact.texts.text10")}
                </button>
                <button 
                  type="submit" 
                  className="booking-form-button" 
                  onClick={() => setContactMethod('email')}
                  disabled={subject === '' || name === '' || (subjectId === '2' && body.trim() === '')}
                >
                  {t("pages.contact.texts.text11")}
                </button>
              </div>
            )}
        </form>
        {isModalOpen && <ContactModal 
          t={t} 
          contact={{
            name: name, 
            subject: subject, 
            body: body, 
            isSent: isSuccess, 
            startDate: startDate, 
            endDate: endDate,
            startTime: startTime,
            endTime: endTime,
            subjectId: subjectId,
            price: calculateInfoButton()
          }}
          onClose={handleCloseModal}
        />}
      </>
    );
  };
  
  export default ContactForm;
  const getFutureDate = (days) => {
    const today = dateInSpain();
  
    const nextDay = new Date(today);
    nextDay.setDate(today.getDate() + days); 
    return nextDay.toISOString().split('T')[0]; // Formato YYYY-MM-DD
  }
  const getDateRange = (type) => {
    const today = dateInSpain();
    let startDate, endDate;
  
    const nextMonday = new Date(today);
    nextMonday.setDate(today.getDate() + ((1 - today.getDay() + 7) % 7 || 7)); 
    startDate = nextMonday;

    if (type === "week") {
      const nextFriday = new Date(nextMonday);
      nextFriday.setDate(nextMonday.getDate() + 4);
      endDate = nextFriday;
    } else if (type === "month") {
      const endDateRaw = new Date(nextMonday);
      endDateRaw.setDate(nextMonday.getDate() + 30);
      endDate = endDateRaw;
    } else {
      endDate = "DD/MM/YYYY";
    }
    
    return {
      startDate: formatDate(startDate),
      endDate: endDate !== "DD/MM/YYYY" ? formatDate(endDate) : endDate
    };
  };

  const formatDate = (date) => {
    const locale = 'es-ES';
    return date.toLocaleDateString(locale);
  };

  const prepareBody = (key, value) => {
    const dateRange = getDateRange(key);
    const startDate = dateRange.startDate;
    const endDate = dateRange.endDate;

    return value.model
        .replace('%startDate%', startDate)
        .replace('%endDate%', endDate);
  };